import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react'
import {
  AuthContextType,
  AuthScope,
  AuthState,
  LoginCredentials,
  LoginResult,
  OtpChannel,
  OtpContactVerificationResponse,
  OtpStatusResponse,
  PortalKey,
  RegisterData,
  User,
  UserRole
} from '../types'
import { authApi } from '../services/authApi'
import type { AuthResponse, AuthUserPayload } from '../services/authApi'
import { bffLogout, silentReseat } from '../services/retailBff'
import { isDhruvantaBffEnabled, variantForPortal } from '../config/dhruvantaAuth'
import { statusOf } from '../services/api'

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
  portal?: PortalKey
}

const getPortalFromDom = (): PortalKey | undefined => {
  const value = document.body.dataset.portal
  if (!value) {
    return undefined
  }
  if (['customer', 'seller', 'delivery', 'operations', 'admin'].includes(value)) {
    return value as PortalKey
  }
  return undefined
}

const USER_ROLE_VALUES: UserRole[] = [
  'CUSTOMER',
  'SELLER',
  'DELIVERY_AGENT',
  'ADMIN', // Ensure ADMIN is recognized as a valid base role
  'RETAIL_MANAGER',
  'CUSTOMER_CARE',
  'SUPER_ADMIN',
  'AUDITOR',
  'STORE_MANAGER',
  'STORE_CASHIER',
  'STORE_DISTRIBUTOR',
  'STORE_ARRANGER'
]

const ROLE_SYNONYMS: Record<string, UserRole> = {
  ADMIN: 'SUPER_ADMIN', // Critical mapping for V2
  ADMIN_EXECUTIVE: 'SUPER_ADMIN',
  EXECUTIVE: 'SUPER_ADMIN',
  EXECUTIVE_ADMIN: 'SUPER_ADMIN',
  PLATFORM_ADMIN: 'SUPER_ADMIN',
  SYSTEM_ADMIN: 'SUPER_ADMIN',
  GLOBAL_ADMIN: 'SUPER_ADMIN'
}

const inferRoleFromScopes = (scopes?: string[] | null): UserRole | null => {
  if (!scopes?.length) {
    return null
  }

  const normalized = scopes.map(scope => scope.toLowerCase())
  const hasAll = (required: string[]) => required.every(scope => normalized.includes(scope))

  // V2 Scope Inference
  if (normalized.includes('admin:super')) {
    return 'SUPER_ADMIN'
  }

  if (hasAll(['identity:manage', 'audit:export'])) {
    return 'SUPER_ADMIN'
  }

  return null
}

const normalizeRoleValue = (value?: string | null): UserRole | null => {
  if (!value) {
    return null
  }

  const sanitized = value.toString().trim()
  if (!sanitized) {
    return null
  }

  const upper = sanitized.replace(/[\s-]+/g, '_').toUpperCase()
  if (ROLE_SYNONYMS[upper]) {
    return ROLE_SYNONYMS[upper]
  }
  if (USER_ROLE_VALUES.includes(upper as UserRole)) {
    return upper as UserRole
  }

  const collapsed = upper.replace(/_/g, '')
  if (ROLE_SYNONYMS[collapsed]) {
    return ROLE_SYNONYMS[collapsed]
  }

  const fuzzyMatch = USER_ROLE_VALUES.find(role => role.replace(/_/g, '') === collapsed)
  return (fuzzyMatch as UserRole | undefined) ?? null
}

const parseStoredRole = (value: string | null): UserRole | null => normalizeRoleValue(value)

const resolveUserRole = (
  payloadRole?: string | null,
  storedRole?: string | null,
  previousRole?: string | null,
  payloadScopes?: string[] | null
): UserRole => {
  const fromPayload = normalizeRoleValue(payloadRole)
  if (fromPayload) {
    return fromPayload
  }

  const inferred = inferRoleFromScopes(payloadScopes)
  if (inferred) {
    return inferred
  }

  const fromStorage = normalizeRoleValue(storedRole)
  if (fromStorage) {
    return fromStorage
  }
  const fromPrevious = normalizeRoleValue(previousRole)
  if (fromPrevious) {
    return fromPrevious
  }
  return 'CUSTOMER'
}

type AuthResponseWithLegacyFields = AuthResponse & {
  role?: string | null
  userId?: string | null
  sub?: string | null
}

const buildUserSnapshot = (
  payload: AuthResponse,
  previousUser: User | null,
  storedRole: UserRole | null
) => {
  const payloadWithLegacyFields = payload as AuthResponseWithLegacyFields
  // Legacy V1 auth responses returned user fields at the top level instead of under `user`; when
  // `payload.user` is absent we read those flattened fields. The double-cast (through `unknown`)
  // reflects that this is an intentional legacy-shape reinterpretation, not a structural overlap.
  const userPayload = payload.user || (payloadWithLegacyFields as unknown as AuthUserPayload)

  const roleFromPayload = userPayload.role || payloadWithLegacyFields.role
  const scopesFromPayload = userPayload.scopes || payload.scopes

  const resolvedRole = resolveUserRole(
    roleFromPayload ?? null,
    storedRole,
    previousUser?.role ?? null,
    scopesFromPayload ?? null
  )
  const resolvedScopes = scopesFromPayload ?? previousUser?.scopes ?? []
  const resolvedStatus = userPayload.status ?? previousUser?.status ?? 'ACTIVE'

  const mergedUser: User = {
    ...(previousUser ?? {}),
    ...userPayload,
    id: userPayload.id || payloadWithLegacyFields.userId || previousUser?.id || '',
    email: userPayload.email || payloadWithLegacyFields.sub || previousUser?.email || '',
    name: userPayload.name || previousUser?.name || '',
    authScope: userPayload.authScope || 'global',
    createdAt: userPayload.createdAt || new Date().toISOString(),
    role: resolvedRole,
    status: resolvedStatus,
    scopes: resolvedScopes,
    mfaEnabled: userPayload.mfaEnabled ?? previousUser?.mfaEnabled
  }

  return {
    user: mergedUser,
    role: resolvedRole,
    scopes: resolvedScopes
  }
}

export function AuthProvider({ children, portal }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    authScope: null,
    role: null,
    scopes: [],
    portal: portal ?? getPortalFromDom(),
    tokenExpiresAt: null,
    pendingMfa: false,
    otpChallengeId: null,
    otpChannel: null,
    otpEnrolled: null,
    otpEmailStatus: null,
    otpSmsStatus: null,
    otpChannels: null,
    portalForbidden: false
  })

  /**
   * KAN-2036 — the boot silent-re-seat is attempted AT MOST ONCE per mount. Without
   * this guard a portal whose retail cookie cannot be re-seated would loop
   * boot -> re-seat -> 401 -> re-seat forever.
   */
  const silentReseatAttempted = useRef(false)

  const resolvePortal = useCallback(() => portal ?? getPortalFromDom(), [portal])

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('authScope')
    localStorage.removeItem('userRole')
    localStorage.removeItem('authPortal')
    localStorage.removeItem('authScopes')
    localStorage.removeItem('tokenExpiresAt')
  }, [])

  const checkAuth = useCallback(() => {
    const run = async () => {
      try {
        const storedAuthScope = localStorage.getItem('authScope')
        const allowedScopes: AuthScope[] = ['global', 'retail', 'family-tree', 'seller']
        const authScope: AuthScope | null = allowedScopes.includes(storedAuthScope as AuthScope)
          ? (storedAuthScope as AuthScope)
          : null
        const storedRole = parseStoredRole(localStorage.getItem('userRole'))
        const tokenExpiresAt = localStorage.getItem('tokenExpiresAt')
        const storedPortal = localStorage.getItem('authPortal') as PortalKey | null
        const resolvedPortal = resolvePortal() ?? storedPortal ?? undefined

        const applyState = (user: User, role: UserRole, scopes: string[], expiresAt: string | null) => {
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            authScope,
            role,
            scopes,
            portal: resolvedPortal,
            tokenExpiresAt: expiresAt,
            pendingMfa: false,
            otpChallengeId: null,
            otpChannel: null,
            otpEnrolled: null,
            otpEmailStatus: null,
            otpSmsStatus: null,
            otpChannels: null,
            portalForbidden: false
          })
        }

        const hydrateFromPayload = (payload: AuthResponse['user']) => {
          const synthetic = { user: payload } as AuthResponse
          const { user: resolvedUser, role: resolvedRole, scopes } = buildUserSnapshot(
            synthetic,
            authState.user,
            storedRole
          )
          localStorage.removeItem('authToken')
          localStorage.removeItem('refreshToken')
          localStorage.setItem('user', JSON.stringify(resolvedUser))
          localStorage.setItem('authScopes', JSON.stringify(scopes))
          localStorage.setItem('userRole', resolvedRole)
          applyState(resolvedUser, resolvedRole, scopes, tokenExpiresAt)
        }

        try {
          const current = await authApi.getCurrentUser()
          hydrateFromPayload(current)
          return
        } catch (error) {
          if (import.meta.env.DEV) {
            console.error('Failed to load current user during auth check:', error)
          }

          // THE 401 vs 403 CONTRACT (KAN-2036) — see src/services/api.ts.
          //
          // A 403 here means the session IS valid but this identity is not entitled
          // to THIS portal. Falling through to clearStoredSession() would destroy a
          // perfectly good session (the exact PrintAnywhere regression this contract
          // exists to prevent). Leave the session alone; the portal guard renders the
          // "no access to this portal" screen inline.
          if (statusOf(error) === 403) {
            setAuthState(prev => ({
              ...prev,
              isLoading: false,
              portal: resolvedPortal,
              portalForbidden: true
            }))
            return
          }

          // A 401 on the FIRST boot probe, on a portal running the Dhruvanta BFF
          // path, gets ONE silent server-side re-seat: the broker rotates the sealed
          // app-scoped tokens and re-calls the retail bridge so the retail cookie is
          // seated again — no token ever reaches the browser. Guarded by a ref so
          // there is no boot -> re-seat -> 401 -> re-seat loop.
          if (
            statusOf(error) === 401 &&
            isDhruvantaBffEnabled() &&
            !silentReseatAttempted.current
          ) {
            silentReseatAttempted.current = true
            const reseated = await silentReseat(variantForPortal(resolvedPortal)).catch(() => false)
            if (reseated) {
              try {
                const restored = await authApi.getCurrentUser()
                hydrateFromPayload(restored)
                return
              } catch {
                // Fall through to the signed-out state below.
              }
            }
          }
        }

        clearStoredSession()

        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          isAuthenticated: false,
          user: null,
          authScope,
          role: null,
          scopes: [],
          portal: resolvedPortal,
          tokenExpiresAt: null,
          pendingMfa: false,
          otpChallengeId: null,
          otpChannel: null,
          otpEnrolled: null,
          otpEmailStatus: null,
          otpSmsStatus: null,
          otpChannels: null,
          portalForbidden: false
        }))
      } catch (error) {
        console.error('Auth check failed:', error)
        clearStoredSession()
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          scopes: [],
          portal: resolvePortal(),
          pendingMfa: false,
          otpChallengeId: null,
          otpChannel: null,
          otpEnrolled: null,
          otpEmailStatus: null,
          otpSmsStatus: null,
          otpChannels: null
        }))
      }
    }

    void run()
  }, [authState.user, clearStoredSession, resolvePortal])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  /**
   * "Sign in with Dhruvanta", TOKEN-FREE (KAN-2036) — the PRIMARY path.
   *
   * By the time this runs the whole ceremony is already finished SERVER-SIDE: the
   * same-origin BFF broker drove the IdP login, sealed the IdP tokens in its httpOnly
   * cookie, and called the retail bridge server-to-server so the retail session
   * cookie is seated. The SPA therefore has exactly one job — re-hydrate from that
   * cookie. There is no token to receive, pass, or store.
   */
  const signInWithDhruvantaBff = useCallback(async (): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true, portalForbidden: false }))
    // A fresh sign-in re-arms the one-shot boot re-seat for the new session.
    silentReseatAttempted.current = false
    checkAuth()
  }, [checkAuth])

  // TODO(deprecation): to be deprecated soon in favour of `signInWithDhruvantaBff`
  // (KAN-2036); do not build new work on this. It accepts LIVE IdP tokens from the
  // browser, which is the posture the BFF broker exists to remove. Retained only for
  // the `VITE_DHRUVANTA_AUTH_MODE=legacy` rollback.
  //
  // "Sign in with Dhruvanta" (KAN-668 / KAN-747): the FE obtains the IdP access + id tokens via the
  // API-first login (no redirect) and posts both; the bridge mints a cookie session, so we just
  // hydrate state from the cookie session via checkAuth() — no token juggling in the SPA.
  const loginWithDhruvanta = useCallback(
    async (accessToken: string, idToken: string): Promise<void> => {
      setAuthState(prev => ({ ...prev, isLoading: true }))
      try {
        await authApi.signInWithDhruvanta(accessToken, idToken)
      } catch (error) {
        setAuthState(prev => ({ ...prev, isLoading: false }))
        throw error
      }
      checkAuth()
    },
    [checkAuth],
  )

  const applyOtpStatus = useCallback((status: OtpStatusResponse) => {
    setAuthState(prev => ({
      ...prev,
      otpEnrolled: status.enrolled,
      otpEmailStatus: status.emailStatus ?? null,
      otpSmsStatus: status.smsStatus ?? null,
      otpChannels: status.channels ?? null
    }))
  }, [])

  const refreshOtpStatus = useCallback(async (): Promise<OtpStatusResponse> => {
    const status = await authApi.getOtpStatus()
    applyOtpStatus(status)
    return status
  }, [applyOtpStatus])

  const challengeOtpContact = useCallback(async (
    channel: OtpChannel,
    metadata?: Record<string, string>
  ): Promise<OtpContactVerificationResponse> => {
    const response = await authApi.challengeOtpContact({ channel, metadata })

    setAuthState(prev => ({
      ...prev,
      otpChallengeId: response.challengeId,
      otpChannel: response.channel ?? channel
    }))

    return response
  }, [])

  const confirmOtpContact = useCallback(async (
    challengeId: string,
    code: string
  ): Promise<OtpStatusResponse> => {
    const status = await authApi.confirmOtpContact({ challengeId, code })

    setAuthState(prev => ({
      ...prev,
      otpChallengeId: null,
      otpChannel: null,
      otpEnrolled: status.enrolled,
      otpEmailStatus: status.emailStatus ?? null,
      otpSmsStatus: status.smsStatus ?? null,
      otpChannels: status.channels ?? null
    }))

    return status
  }, [])

  const login = useCallback(async (
    credentials: LoginCredentials,
    scope?: AuthScope,
    requestedScopes: string[] = []
  ): Promise<LoginResult> => {
    setAuthState(prev => ({ ...prev, isLoading: true }))

    const normalizedScope = scope ?? null
    const hasRequestedScopes = requestedScopes.length > 0

    const shouldRetryWithoutScope = (error: unknown): boolean => {
      if (!(error instanceof Error)) return false
      const normalized = error.message.toLowerCase()
      return (
        normalized.includes('requested scopes exceed assigned permissions') ||
        normalized.includes('invalid scope')
      )
    }

    const attemptLogin = async (withScope: boolean, includeRequestedScopes: boolean) =>
      authApi.login({
        email: credentials.email,
        password: credentials.password,
        scope: withScope && normalizedScope ? normalizedScope : undefined,
        requestedScopes: includeRequestedScopes && hasRequestedScopes ? requestedScopes : undefined,
        otpChallengeId: credentials.otpChallengeId,
        otpCode: credentials.otpCode
      })

    let response
    try {
      response = await attemptLogin(Boolean(normalizedScope), hasRequestedScopes)
    } catch (error) {
      if ((normalizedScope || hasRequestedScopes) && shouldRetryWithoutScope(error)) {
        response = await attemptLogin(false, false)
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          pendingMfa: false,
          otpChallengeId: null,
          otpChannel: null,
          otpEnrolled: null,
          otpEmailStatus: null,
          otpSmsStatus: null,
          otpChannels: prev.otpChannels ?? null
        }))
        throw error
      }
    }

    if (!response) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        pendingMfa: false,
        otpChallengeId: null,
        otpChannel: null,
        otpEnrolled: null,
        otpEmailStatus: null,
        otpSmsStatus: null,
        otpChannels: prev.otpChannels ?? null
      }))
      throw new Error('Unable to establish session')
    }

    const challengeId = response.otpChallengeId ?? null

    if (response.otpRequired) {
      const resolvedPortal = resolvePortal()
      setAuthState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authScope: normalizedScope,
        role: null,
        scopes: [],
        portal: resolvedPortal,
        tokenExpiresAt: null,
        pendingMfa: true,
        otpChallengeId: challengeId,
        otpChannel: response.otpChannel ?? null,
        otpEnrolled: response.otpEnrolled ?? null,
        otpEmailStatus: null,
        otpSmsStatus: null,
        otpChannels: prev.otpChannels ?? null
      }))

      return {
        status: 'otp_required',
        challengeId: challengeId ?? '',
        channel: response.otpChannel ?? null,
        enrolled: response.otpEnrolled ?? null
      }
    }

    let finalResponse = response;
    if (!response.user) {
      try {
        const userProfile = await authApi.getCurrentUser();
        finalResponse = {
          ...response,
          user: userProfile
        };
      } catch (err) {
        console.error('Failed to fetch user profile after login:', err);
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          pendingMfa: false,
          otpChallengeId: null,
          otpChannel: null,
          otpEnrolled: null,
          otpEmailStatus: null,
          otpSmsStatus: null,
          otpChannels: prev.otpChannels ?? null
        }))
        throw new Error('Authenticated session was created but the current user profile could not be loaded')
      }
    }

    const expiresAt = finalResponse.tokenExpiresAt ?? null
    const resolvedPortal = resolvePortal()
    const storedRole = parseStoredRole(localStorage.getItem('userRole'))
    const { user: resolvedUser, role: resolvedRole, scopes: resolvedScopes } = buildUserSnapshot(
      finalResponse,
      authState.user,
      storedRole
    )

    if (import.meta.env.DEV) {
      console.debug('[AuthContext] login success', {
        user: resolvedUser,
        scopes: resolvedScopes,
        portal: resolvedPortal,
        otpRequired: finalResponse.otpRequired,
        otpEnrolled: finalResponse.otpEnrolled
      })
    }

    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.setItem('user', JSON.stringify(resolvedUser))
    if (normalizedScope) {
      localStorage.setItem('authScope', normalizedScope)
    } else {
      localStorage.removeItem('authScope')
    }
    localStorage.setItem('authPortal', resolvedPortal ?? '')
    localStorage.setItem('authScopes', JSON.stringify(resolvedScopes))
    if (resolvedRole) {
      localStorage.setItem('userRole', resolvedRole)
    } else {
      localStorage.removeItem('userRole')
    }
    if (expiresAt) {
      localStorage.setItem('tokenExpiresAt', expiresAt)
    } else {
      localStorage.removeItem('tokenExpiresAt')
    }

    setAuthState({
      user: resolvedUser,
      isAuthenticated: true,
      isLoading: false,
      authScope: normalizedScope,
      role: resolvedRole,
      scopes: resolvedScopes,
      portal: resolvedPortal,
      tokenExpiresAt: expiresAt,
      pendingMfa: Boolean(finalResponse.mfaRequired),
      otpChallengeId: null,
      otpChannel: null,
      otpEnrolled: finalResponse.otpEnrolled ?? null,
      otpEmailStatus: null,
      otpSmsStatus: null,
      otpChannels: null
    })

    refreshOtpStatus().catch(error => {
      if (import.meta.env.DEV) {
        console.error('Failed to refresh OTP status after login:', error)
      }
    })

    return { status: 'success' }
  }, [authState.user, refreshOtpStatus, resolvePortal])

  const register = useCallback(async (
    data: RegisterData,
    scope: AuthScope = 'global'
  ): Promise<void> => {
    setAuthState(prev => ({ ...prev, isLoading: true }))

    try {
      // Call backend API
      const response = await authApi.register({
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        fullName: data.name,
        authScope: scope,
        role: data.role,
        // DPDP consent (KAN-780): forwarded so the backend records the consent ledger + enforces
        // that Terms + Privacy were accepted.
        acceptTerms: data.acceptTerms,
        acceptPrivacy: data.acceptPrivacy,
        acceptMarketing: data.acceptMarketing
      })

      let finalResponse = response;
      if (!response.user) {
        try {
          const userProfile = await authApi.getCurrentUser();
          finalResponse = { ...response, user: userProfile };
        } catch (e) {
          console.error('Failed to fetch user profile after register', e);
          throw new Error('Registration completed but the current user profile could not be loaded')
        }
      }

      const resolvedPortal = resolvePortal()
      const storedRole = parseStoredRole(localStorage.getItem('userRole'))
      const { user: resolvedUser, role: resolvedRole, scopes: resolvedScopes } = buildUserSnapshot(
        finalResponse,
        authState.user,
        storedRole
      )

      localStorage.removeItem('authToken')
      localStorage.removeItem('refreshToken')
      localStorage.setItem('user', JSON.stringify(resolvedUser))
      if (scope) {
        localStorage.setItem('authScope', scope)
      } else {
        localStorage.removeItem('authScope')
      }
      if (resolvedRole) {
        localStorage.setItem('userRole', resolvedRole)
      } else {
        localStorage.removeItem('userRole')
      }
      localStorage.setItem('authPortal', resolvedPortal ?? '')
      localStorage.setItem('authScopes', JSON.stringify(resolvedScopes))
      if (finalResponse.tokenExpiresAt) {
        localStorage.setItem('tokenExpiresAt', finalResponse.tokenExpiresAt)
      } else {
        localStorage.removeItem('tokenExpiresAt')
      }

      setAuthState({
        user: resolvedUser,
        isAuthenticated: true,
        isLoading: false,
        authScope: scope,
        role: resolvedRole,
        scopes: resolvedScopes,
        portal: resolvedPortal,
        tokenExpiresAt: finalResponse.tokenExpiresAt ?? null,
        pendingMfa: false,
        otpChallengeId: null,
        otpChannel: null,
        otpEnrolled: null,
        otpEmailStatus: null,
        otpSmsStatus: null,
        otpChannels: null
      })

      refreshOtpStatus().catch(error => {
        if (import.meta.env.DEV) {
          console.error('Failed to refresh OTP status after registration:', error)
        }
      })
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        pendingMfa: false,
        otpChallengeId: null,
        otpChannel: null,
        otpEnrolled: null,
        otpEmailStatus: null,
        otpSmsStatus: null,
        otpChannels: prev.otpChannels ?? null
      }))
      throw error
    }
  }, [authState.user, refreshOtpStatus, resolvePortal])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    }

    // KAN-2036 — also clear the SEALED server-side Dhruvanta container, otherwise the
    // next boot probe would silently re-seat the retail cookie and the user would
    // appear to never sign out. Best-effort by design: `bffLogout` swallows its own
    // failures so a broker hiccup can never block the local sign-out below.
    if (isDhruvantaBffEnabled()) {
      await bffLogout()
    }

    // ALWAYS land signed-out locally, whatever the two network legs above did.
    clearStoredSession()
    silentReseatAttempted.current = false

    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authScope: null,
      role: null,
      scopes: [],
      portal: resolvePortal(),
      tokenExpiresAt: null,
      pendingMfa: false,
      otpChallengeId: null,
      otpChannel: null,
      otpEnrolled: null,
      otpEmailStatus: null,
      otpSmsStatus: null,
      otpChannels: null,
      portalForbidden: false
    })
  }, [clearStoredSession, resolvePortal])

  const value: AuthContextType = useMemo(() => ({
    authState,
    login,
    loginWithDhruvanta,
    signInWithDhruvantaBff,
    register,
    logout,
    checkAuth,
    refreshOtpStatus,
    challengeOtpContact,
    confirmOtpContact
  }), [authState, login, loginWithDhruvanta, signInWithDhruvantaBff, register, logout, checkAuth, refreshOtpStatus, challengeOtpContact, confirmOtpContact])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
